import React, {useEffect, useState} from "react";
import {db} from "../firebase";
import {
  collection,
  getDocs,
  query,
  orderBy,
  doc,
  updateDoc,
} from "firebase/firestore";
import {GridColDef, GridRowClassNameParams, GridRowId} from "@mui/x-data-grid";
import {ToggleOff, ToggleOn} from "@mui/icons-material";
import {StyledGrid} from "../style/styled-grid";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography
} from "@mui/material";

interface Apply {
  id: string;
  phone: string;
  createdAt: number;
  checked: boolean;
  registerName: string;
  studentName: string;
  age: number;
  skill: string;
  rental: boolean;
  date: string;
  etc: string;
  memo: string;
}

const ApplyTable: React.FC = () => {
  const [applies, setApplies] = useState<Apply[]>([]);
  const [selectedRow, setSelectedRow] = useState<Apply | null>(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [editMemo, setEditMemo] = useState<string>("");
  const [editing, setEditing] = useState(false);

  const handleSaveMemo = async () => {
    if (selectedRow) {
      try {
        const applyDoc = doc(db, "apply", selectedRow.id);
        await updateDoc(applyDoc, {memo: editMemo});
        setApplies((prev) =>
          prev.map((row) =>
            row.id === selectedRow.id ? {...row, memo: editMemo} : row
          )
        );
        setSelectedRow((prev) => (prev ? {...prev, memo: editMemo} : null));
        setEditing(false);
      } catch (error) {
        console.error("메모 업데이트 중 오류 발생:", error);
      }
    }
  };

  useEffect(() => {
    const fetchApplies = async () => {
      try {
        const applyCollection = collection(db, "apply");
        const q = query(applyCollection, orderBy("createdAt", "desc"));
        const querySnapshot = await getDocs(q);
        const applyList: Apply[] = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          registerName: doc.data().register_name,
          studentName: doc.data().student_name,
          ...doc.data(),
        })) as Apply[];
        setApplies(applyList);
      } catch (error) {
        console.error("데이터 가져오기 실패:", error);
      }
    };

    fetchApplies();
  }, []);

  const toggleChecked = async (id: string, currentChecked: boolean) => {
    try {
      const applyDoc = doc(db, "apply", id);
      await updateDoc(applyDoc, {checked: !currentChecked});

      setApplies((prev) =>
        prev.map((row) =>
          row.id === id ? {...row, checked: !currentChecked} : row
        )
      );

      if (selectedRow && selectedRow.id === id) {
        setSelectedRow((prev) => (prev ? {...prev, checked: !currentChecked} : null));
      }
    } catch (error) {
      console.error("확인 여부 변경 중 오류 발생:", error);
    }
  };


  const columns: GridColDef[] = [
    {field: "id", headerName: "ID", flex: 0.5},
    {
      field: "createdAt",
      headerName: "등록 일자",
      minWidth: 140,
      valueFormatter: (value) => {
        const date = new Date(value as number);
        const formattedDate = new Intl.DateTimeFormat("ko-KR", {
          year: "2-digit",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }).format(date);
        return formattedDate.replace(/\./g, "").replace(/ /g, ".");
      },
    },
    {
      field: "phone",
      headerName: "전화번호",
      flex: 1,
    },
    {
      field: "checked",
      headerName: "확인 여부",
      flex: 0.6,
      renderCell: (params) => (
        <span
          style={{
            color: params.value ? "#AAAAAA" : "inherit",
            cursor: "pointer",
          }}
          onClick={() => toggleChecked(params.row.id, params.value as boolean)}
        >
          {params.value ? (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <ToggleOn sx={{width: 24, height: 24, color: "#83A7DD"}}/> 확인됨
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <ToggleOff sx={{width: 24, height: 24, color: "#CCCCCC"}}/> 미확인
            </div>
          )}
        </span>
      ),
    },
  ];

  const handleRowClick = (rowId: GridRowId) => {
    const row = applies.find((item) => item.id === rowId);
    if (row) {
      setSelectedRow(row);
      setEditMemo(row.memo);
      setEditing(false);
    }
  };

  const getRowClassName = (params: GridRowClassNameParams) => {
    return params.row.checked ? 'checked' : '';
  };

  return (
    <Box sx={{width: '100%', display: "flex", flexDirection: "row", alignItems: "center", gap: '20px'}}>
      <Box sx={{height: 400, width: "100%"}}>
        <StyledGrid
          rows={applies}
          columns={columns}
          onRowClick={(params) => handleRowClick(params.id)}
          getRowClassName={getRowClassName}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          sx={{minWidth: '500px', width: '500px'}}
        />
      </Box>

      {selectedRow && (
        <Paper
          sx={{
            minWidth: '400px',
            height: "100%",
            mt: 2,
            p: "20px",
            width: "90%",
            maxWidth: 800,
            backgroundColor: "#FFFFFF",
          }}
        >
          <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
            <Typography variant="h6" gutterBottom>
              상세 정보
            </Typography>
            <Button
              onClick={() => toggleChecked(selectedRow.id, selectedRow.checked)}
              variant="outlined"
              startIcon={selectedRow.checked ? <ToggleOn/> : <ToggleOff/>}
              sx={{ml: 2}}
            >
              {selectedRow.checked ? "확인됨" : "미확인"}
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={{fontWeight: "bold", width: "30%"}}>등록자 이름</TableCell>
                  <TableCell>{selectedRow.registerName}</TableCell>
                </TableRow>
                 <TableRow>
                  <TableCell sx={{fontWeight: "bold", width: "30%"}}>전화번호</TableCell>
                   <TableCell>
                     <a
                     href={`tel:${selectedRow.phone}`}
                     style={{textDecoration: "none", color: "inherit", cursor: "pointer"}}
                   >
                     {selectedRow.phone}
                   </a>
                   </TableCell>
                 </TableRow>
                <TableRow>
                  <TableCell sx={{fontWeight: "bold"}}>학생 이름</TableCell>
                  <TableCell>{selectedRow.studentName}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{fontWeight: "bold"}}>나이</TableCell>
                  <TableCell>{selectedRow.age}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{fontWeight: "bold"}}>스킬 수준</TableCell>
                  <TableCell>{selectedRow.skill}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{fontWeight: "bold"}}>장비 대여 여부</TableCell>
                  <TableCell>{selectedRow.rental ? "예" : "아니오"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{fontWeight: "bold"}}>신청 날짜</TableCell>
                  <TableCell>{selectedRow.date}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{fontWeight: "bold"}}>기타 정보</TableCell>
                  <TableCell>{selectedRow.etc}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{fontWeight: "bold"}}>메모</TableCell>
                  <TableCell>
                    {editing ? (
                      <>
                        <TextField
                          value={editMemo}
                          onChange={(e) => setEditMemo(e.target.value)}
                          size="medium"
                          fullWidth
                        />
                        <Button onClick={handleSaveMemo} variant="contained" sx={{mt: 1, border: '#88BBE5'}}>
                          저장
                        </Button>
                      </>
                    ) : (
                      <Typography onClick={() => setEditing(true)} sx={{cursor: "pointer"}}>
                        {selectedRow.memo || "클릭해 메모를 입력하세요"}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

      )}
    </Box>
  );
};

export default ApplyTable;
